import type { TopUpOptions } from "@uzverse/contracts";
import { useToast } from "@uzverse/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVehicleT } from "./i18n.js";
import { useVehicles, vehicleStore } from "./store.js";

const fmt = (n: number): string => Math.round(n).toLocaleString("en-US").replace(/,/g, ".");

/** What `credits` cost with a paid provider, formatted for the locale; null in free mode. */
export function priceOf(options: TopUpOptions, credits: number, locale: string): string | null {
  if (options.free) return null;
  const minor = Math.ceil((credits / 1000) * options.prices[options.currency]);
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: options.currency }).format(
      minor / 100,
    );
  } catch {
    return `${(minor / 100).toFixed(2)} ${options.currency}`;
  }
}

/** The balance in the world HUD with a "Kredi yükle" button. Hidden while driving. */
export function WalletPill() {
  const t = useVehicleT();
  const credits = useVehicles((s) => s.credits);
  const driving = useVehicles((s) => s.drivingId);
  const galeri = useVehicles((s) => s.galeri.open);
  if (credits === null || driving || galeri) return null;
  return (
    <div className="veh-pill" data-testid="wallet-pill">
      <span className="veh-pill-n" data-testid="wallet-pill-balance" title={t("wallet")}>
        {t("credits", { n: fmt(credits) })}
      </span>
      <button
        type="button"
        className="veh-pill-add"
        data-testid="topup-open"
        onClick={() => vehicleStore.openTopUp()}
      >
        {t("addCredits")}
      </button>
    </div>
  );
}

/** The "Kredi yükle" button the Galeri panel shows next to the balance. */
export function TopUpButton() {
  const t = useVehicleT();
  return (
    <button
      type="button"
      className="veh-topup-btn"
      data-testid="galeri-topup"
      onClick={() => vehicleStore.openTopUp()}
    >
      {t("addCredits")}
    </button>
  );
}

/** Packages and a custom amount, a history tab; completes instantly in free mode. */
export function TopUpDialog() {
  const t = useVehicleT();
  const { i18n } = useTranslation();
  const toast = useToast();
  const ui = useVehicles((s) => s.topUp);
  const credits = useVehicles((s) => s.credits);
  const [picked, setPicked] = useState<string>("p50k");
  const [custom, setCustom] = useState("");

  useEffect(() => {
    if (!ui.open) return;
    const esc = (e: KeyboardEvent) => {
      if (e.code === "Escape") vehicleStore.closeTopUp();
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [ui.open]);

  const options = ui.options;
  useEffect(() => {
    if (options && picked !== "custom" && !options.packages.some((p) => p.id === picked)) {
      setPicked(options.packages[1]?.id ?? options.packages[0]?.id ?? "custom");
    }
  }, [options, picked]);

  if (!ui.open) return null;
  const pkg = options?.packages.find((p) => p.id === picked);
  const customN = Number(custom);
  const customOk =
    options !== null &&
    Number.isInteger(customN) &&
    customN >= options.min &&
    customN <= options.max;
  const amount = picked === "custom" ? (customOk ? customN : 0) : (pkg?.credits ?? 0);
  const price = options && amount > 0 ? priceOf(options, amount, i18n.language) : null;

  const submit = async () => {
    if (!options || amount <= 0 || ui.busy) return;
    const done = await vehicleStore.topUp(
      picked === "custom" ? { credits: amount } : { packageId: picked },
    );
    if (done === "completed") toast.show(t("toppedUp", { n: fmt(amount) }));
  };

  return (
    <div className="veh-modal" data-testid="topup-scrim">
      <div
        className="veh-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t("topUpTitle")}
        data-testid="topup-dialog"
      >
        <header className="veh-panel-head">
          <h2>{t("topUpTitle")}</h2>
          {options?.free ? (
            <span className="veh-free" data-testid="topup-free-badge">
              {t("freeNow")}
            </span>
          ) : null}
          <button
            type="button"
            className="veh-x"
            data-testid="topup-close"
            aria-label={t("close")}
            onClick={() => vehicleStore.closeTopUp()}
          >
            ×
          </button>
        </header>
        <p className="veh-sub" data-testid="topup-balance">
          {credits === null ? "–" : t("balanceNow", { n: fmt(credits) })}
        </p>
        <div className="veh-tabs" role="tablist">
          {(["buy", "history"] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={ui.tab === k}
              data-testid={`topup-tab-${k}`}
              onClick={() => vehicleStore.setTopUpTab(k)}
            >
              {k === "buy" ? t("tabBuy") : t("tabHistory")}
            </button>
          ))}
        </div>
        {ui.tab === "history" ? (
          <ul className="veh-history" data-testid="topup-history">
            {ui.history.length === 0 ? <li className="veh-empty">{t("historyEmpty")}</li> : null}
            {ui.history.map((e) => (
              <li key={e.id} data-testid="topup-history-item" data-kind={e.kind}>
                <span>{t(`entry.${e.kind}`)}</span>
                <span className="veh-sub">{e.note ?? ""}</span>
                <b className={e.credits >= 0 ? "veh-good" : "veh-bad"}>
                  {e.credits >= 0 ? "+" : "−"}
                  {fmt(Math.abs(e.credits))}
                </b>
              </li>
            ))}
          </ul>
        ) : !options ? (
          <p className="veh-empty">…</p>
        ) : (
          <div className="veh-topup-body">
            <p className="veh-sub">{options.free ? t("freeNote") : t("paidNote")}</p>
            <div className="veh-packs" role="radiogroup" aria-label={t("packagesLabel")}>
              {options.packages.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={picked === p.id}
                  data-testid={`topup-package-${p.id}`}
                  onClick={() => setPicked(p.id)}
                >
                  <b>{t("credits", { n: fmt(p.credits) })}</b>
                  {options.free ? (
                    <small>{t("freeNow")}</small>
                  ) : (
                    <small>{priceOf(options, p.credits, i18n.language)}</small>
                  )}
                </button>
              ))}
              <button
                type="button"
                role="radio"
                aria-checked={picked === "custom"}
                data-testid="topup-package-custom"
                onClick={() => setPicked("custom")}
              >
                <b>{t("customAmount")}</b>
                <small>{t("customHint", { min: fmt(options.min), max: fmt(options.max) })}</small>
              </button>
            </div>
            {picked === "custom" ? (
              <label className="veh-custom">
                <span>{t("customAmount")}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={options.min}
                  max={options.max}
                  step={1}
                  value={custom}
                  data-testid="topup-custom"
                  onChange={(e) => setCustom(e.target.value)}
                />
              </label>
            ) : null}
            <button
              type="button"
              className="veh-buy"
              data-testid="topup-submit"
              disabled={ui.busy || amount <= 0 || ui.pending !== null}
              onClick={() => void submit()}
            >
              {price
                ? t("topUpConfirmPaid", { n: fmt(amount), price })
                : t("topUpConfirm", { n: fmt(amount) })}
            </button>
            {ui.pending ? (
              <p className="veh-note" role="status" data-testid="topup-pending">
                {t("waitingPayment")}{" "}
                <a href={ui.pending.url} target="_blank" rel="noopener noreferrer">
                  {t("openCheckout")}
                </a>{" "}
                <button
                  type="button"
                  className="veh-link"
                  onClick={() => vehicleStore.cancelPending()}
                >
                  {t("cancelWaiting")}
                </button>
              </p>
            ) : null}
            {ui.lastAdded !== null ? (
              <p className="veh-note veh-good" role="status" data-testid="topup-done">
                {t("toppedUp", { n: fmt(ui.lastAdded) })}
              </p>
            ) : null}
            {ui.error ? (
              <p className="veh-note veh-bad" role="alert" data-testid="topup-error">
                {t(ui.error)}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
